import { TechemTheme } from '@techem/techem-theme';
import AnimatedExpandingView from 'presentation/components/AnimatedExpandingView';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardStepsContainer } from 'presentation/components/WizardSteps';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { paths } from 'frameworks_drivers/routes';
import store from 'store';
import { styled } from 'styletron-react';
import { translationKeyForCheckboxOption } from 'tools/CheckboxOptionMapper';
import { summaryTranslationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TableEntry from './component/TableEntry';
import EditBillingPeriodView from './EditBillingPeriodView';
import EditContactDetailsView from './EditContactDetailsView';
import EditHouseAddressView from './EditHouseAddressView';
import EditInvoiceAddressView from './EditInvoiceAddressView';
import HorizontalLine from './component/HorizontalLine';
import SectionTitle from './component/SectionTitle';
import ServiceContent from './component/service-content/ServiceContent';
import PricingContent from './component/pricing-content/PricingContent';
import EditIcon from './component/EditIcon';
import ErrorModal from './component/ErrorModal';
import TermsAndConditionModal from './component/TermsAndConditionModal';
import SectionSeparator from './component/styled-components/SectionSeparator';
import SubmitView from './component/SubmitView';
import useSummaryPageContext from '../../../../frameworks_drivers/context/SummaryPageContext';
import { Products } from '../../../../entities/model/Product';

const CenteringCol = styled(Col, {
  lineHeight: '24px'
});

const Card = styled(Row, {
  paddingTop: '32px',
  paddingRight: '32px',
  paddingBottom: '32px',
  paddingLeft: '32px',
  backgroundColor: TechemTheme.colors.backgroundSecondary,
  color: TechemTheme.colors.contentPrimary,
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
  boxSizing: 'border-box'
});

const Paragraph = styled('p', {
  lineHeight: '26px'
});

function formatPrice(t: any, price?: number) {
  if (!price || price <= 0) {
    return t('freePriceText');
  }
  const localPrice = price!.toLocaleString('de-DE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });
  return t('fullPriceText', { priceTerm: localPrice });
}

function formatPriceForAdditionalService(t: any, price?: number) {
  const prefix = !price || price <= 0 ? '' : '+ ';
  const priceText = formatPrice(t, price);
  return prefix + priceText;
}

var uniqueElementCounter = 0;

function generateUniqueId() {
  return 'id_' + uniqueElementCounter++;
}

const SummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevPath = paths.BillingPeriod;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      !store.getState().onboardingInfo.billingPeriod.startDate
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [onboardingInfo, setOnboardingInfo] = useState(store.getState().onboardingInfo);

  const { isTermsOfServiceModalOpen, setIsTermsOfServiceModalOpen } = useSummaryPageContext();

  const [error, setError] = useState<any>(null);

  const [isEditingContactDetails, setEditingContactDetails] = useState(false);
  const [isEditingInvoiceAddress, setEditingInvoiceAddress] = useState(false);
  const [isEditingHouseAddress, setEditingHouseAddress] = useState(false);
  const [isEditingBillingPeriod, setEditingBillingPeriod] = useState(false);

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  // TODO: Move to ServiceContent component
  const additionalServicesSection = () => {
    const additionalProducts = onboardingInfo.selectedProducts.filter(
      (item) => ![Products.HeatingCosts, Products.HotWaterInvoicing].includes(item.name)
    );

    if (additionalProducts.length === 0) {
      return <></>;
    } else {
      const sectionComponents = [
        <SectionSeparator key="seperator" />,
        <SectionTitle key="title" title={t('pageSummaryAdditionalServicesSectionTitle')} />,
        <HorizontalLine key="horizontal-line" />
      ];

      additionalProducts.forEach((item) => {
        let translationKey = '';
        let testingId = '';
        switch (item.name) {
          case Products.FullOperatingCosts:
            translationKey = 'pageSummaryAdditionalServicesSectionLabelFullOperatingCosts';
            testingId = 'option-full-operating-costs';
            break;
          case Products.ColdWaterInvoicing:
            translationKey = 'pageSummaryAdditionalServicesSectionLabelWaterConsumption';
            testingId = 'option-cold-water-invoicing';
            break;
          case Products.ProviderChangeService:
            translationKey = 'pageSummaryAdditionalServicesSectionLabelProviderChange';
            testingId = 'option-provider-change';
            break;
        }
        sectionComponents.push(
          <>
            <TableEntry
              key={'entry_' + item.name}
              leftValues={[t(translationKey)]}
              endElement={
                <p
                  className="fw-bold mx-0 my-auto"
                  style={{
                    color: `#007c94 !important`
                  }}
                >
                  {formatPriceForAdditionalService(t, item.price)}
                </p>
              }
              additionalTestId={testingId}
            />
            <HorizontalLine key={'line_' + item.name} />
          </>
        );
      });

      return sectionComponents;
    }
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer />
      <TitleWithPrevPageLink onPrevClick={handlePrevBtnClicked} title={t('pageSummaryTitle')} />

      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <SectionSeparator />
          <SectionTitle title={t('pageSummaryCollectedDataSectionTitle')} />

          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelNumberOfApartments')]}
            rightValues={[
              `${onboardingInfo.numberOfApartments} ${t(
                'pageSummaryCollectedDataSectionLabelApartments'
              )}`
            ]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="apartment-count"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelPreviousInvoice')]}
            rightValues={[
              t(summaryTranslationKeyForRadioOption(onboardingInfo.heatingCostsInvoice))
            ]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="thermal-metering"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelCentralHeating')]}
            rightValues={[
              t(summaryTranslationKeyForRadioOption(onboardingInfo.centralHeatingAvailable))
            ]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="central-heating"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelHotWaterDelivery')]}
            rightValues={[
              t(summaryTranslationKeyForRadioOption(onboardingInfo.centralHotWaterDelivery))
            ]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="hot-water-delivery"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelHeatingType')]}
            rightValues={[t(summaryTranslationKeyForRadioOption(onboardingInfo.heatingType))]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="heating-type"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelHeatingSources')]}
            rightValues={[
              onboardingInfo.heatingSources
                .map((heatingSourceKey) => {
                  return t(translationKeyForCheckboxOption(heatingSourceKey));
                })
                .join(', ')
            ]}
            endElement={<div style={{ width: '14px' }}></div>}
            additionalTestId="heating-sources"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelContactDetails')]}
            rightElement={
              <CenteringCol xs={12} md data-gi="right-col" className="d-flex mt-3 mt-md-0">
                <AnimatedExpandingView skipInitialFadeIn>
                  {isEditingContactDetails ? (
                    <Row key="edit-contact-details-view">
                      <EditContactDetailsView
                        callbackOnAbort={() => setEditingContactDetails(false)}
                        callbackOnSuccess={() => {
                          setOnboardingInfo(store.getState().onboardingInfo);
                          setEditingContactDetails(false);
                        }}
                      />
                    </Row>
                  ) : (
                    <Row key="contact-details-view">
                      <Col xs={12}>
                        <Row>
                          <Col xs={12} key={generateUniqueId()} data-gi="0">
                            <p className="fw-bold mx-0 my-auto">
                              {t(
                                summaryTranslationKeyForRadioOption(
                                  onboardingInfo.contactDetails.gender
                                )
                              )}
                            </p>
                          </Col>
                          <Col xs={12} key={generateUniqueId()} data-gi="1">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.contactDetails.fname +
                                ' ' +
                                onboardingInfo.contactDetails.lname}
                            </p>
                          </Col>
                          <Col xs={12} key={generateUniqueId()} data-gi="2">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.contactDetails.phone}
                            </p>
                          </Col>
                          <Col xs={12} key={generateUniqueId()} data-gi="3">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.contactDetails.email}
                            </p>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </AnimatedExpandingView>
              </CenteringCol>
            }
            endElement={
              <div className="mt-1">
                {!isEditingContactDetails ? (
                  <EditIcon
                    alt={'Zum Editieren der Kontaktdaten klicken oder drücken Sie die Leertaste.'}
                    onClick={() => setEditingContactDetails(true)}
                    onKeyDown={(event) => {
                      if (event.key === ' ') {
                        event.preventDefault();
                        setEditingContactDetails(true);
                      }
                    }}
                  />
                ) : (
                  <div style={{ width: '14px' }}></div>
                )}
              </div>
            }
            additionalTestId="contact-details"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelPersonalAddress')]}
            rightElement={
              <CenteringCol xs={12} md data-gi="right-col" className="d-flex mt-3 mt-md-0">
                <AnimatedExpandingView skipInitialFadeIn>
                  {isEditingInvoiceAddress ? (
                    <Row key="edit-invoice-address-view">
                      <EditInvoiceAddressView
                        callbackOnAbort={() => setEditingInvoiceAddress(false)}
                        callbackOnSuccess={() => {
                          setOnboardingInfo(store.getState().onboardingInfo);
                          setEditingInvoiceAddress(false);
                        }}
                      />
                    </Row>
                  ) : (
                    <Row key="invoice-address-view">
                      <Col xs={12}>
                        <Row>
                          <Col xs={12} key={generateUniqueId()} data-gi="0">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.invoiceAddress.street +
                                ' ' +
                                onboardingInfo.invoiceAddress.houseNumber}
                            </p>
                          </Col>
                          <Col xs={12} key={generateUniqueId()} data-gi="1">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.invoiceAddress.zip +
                                ' ' +
                                onboardingInfo.invoiceAddress.city}
                            </p>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </AnimatedExpandingView>
              </CenteringCol>
            }
            endElement={
              <div className="mt-1">
                {!isEditingInvoiceAddress ? (
                  <EditIcon
                    alt={
                      'Zum Editieren der Rechnungsadresse klicken oder drücken Sie die Leertaste.'
                    }
                    onClick={() => setEditingInvoiceAddress(true)}
                    onKeyDown={(event) => {
                      if (event.key === ' ') {
                        event.preventDefault();
                        setEditingInvoiceAddress(true);
                      }
                    }}
                  />
                ) : (
                  <div style={{ width: '14px' }}></div>
                )}
              </div>
            }
            additionalTestId="invoice-address"
          />
          <HorizontalLine />
          <TableEntry
            leftValues={[t('pageSummaryCollectedDataSectionLabelHouseAddress')]}
            rightElement={
              <CenteringCol xs={12} md data-gi="right-col" className="d-flex mt-3 mt-md-0">
                <AnimatedExpandingView skipInitialFadeIn>
                  {isEditingHouseAddress ? (
                    <Row key="edit-house-address-view">
                      <EditHouseAddressView
                        callbackOnAbort={() => setEditingHouseAddress(false)}
                        callbackOnSuccess={() => {
                          setOnboardingInfo(store.getState().onboardingInfo);
                          setEditingHouseAddress(false);
                        }}
                      />
                    </Row>
                  ) : (
                    <Row key="house-address-view">
                      <Col xs={12}>
                        <Row>
                          <Col xs={12} key={generateUniqueId()} data-gi="0">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.houseAddress.street +
                                ' ' +
                                onboardingInfo.houseAddress.houseNumber}
                            </p>
                          </Col>
                          <Col xs={12} key={generateUniqueId()} data-gi="1">
                            <p className="fw-bold mx-0 my-auto">
                              {onboardingInfo.houseAddress.zip +
                                ' ' +
                                onboardingInfo.houseAddress.city}
                            </p>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </AnimatedExpandingView>
              </CenteringCol>
            }
            endElement={
              <div className="mt-1">
                {!isEditingHouseAddress ? (
                  <EditIcon
                    alt={'Zum Editieren der Hausadresse klicken oder drücken Sie die Leertaste.'}
                    onClick={() => setEditingHouseAddress(true)}
                    onKeyDown={(event) => {
                      if (event.key === ' ') {
                        event.preventDefault();
                        setEditingHouseAddress(true);
                      }
                    }}
                  />
                ) : (
                  <div style={{ width: '14px' }}></div>
                )}
              </div>
            }
            additionalTestId="house-address"
          />
          <HorizontalLine />
          {onboardingInfo.billingPeriod && onboardingInfo.billingPeriod.startDate && (
            <>
              <TableEntry
                leftValues={[t('pageSummaryCollectedDataSectionLabelBillingPeriod')]}
                rightElement={
                  <CenteringCol xs={12} md data-gi="right-col" className="d-flex mt-3 mt-md-0">
                    <AnimatedExpandingView skipInitialFadeIn>
                      {isEditingBillingPeriod ? (
                        <Row key="edit-billing-period-view">
                          <EditBillingPeriodView
                            callbackOnAbort={() => setEditingBillingPeriod(false)}
                            callbackOnSuccess={() => {
                              setOnboardingInfo(store.getState().onboardingInfo);
                              setEditingBillingPeriod(false);
                            }}
                          />
                        </Row>
                      ) : (
                        <Row key="billing-period-view">
                          <Col xs={12}>
                            <Row>
                              <Col xs={12} key={generateUniqueId()} data-gi="0">
                                <p className="fw-bold mx-0 my-auto">
                                  {onboardingInfo.billingPeriod.startDate &&
                                  onboardingInfo.billingPeriod.endDate
                                    ? `${new Date(
                                        onboardingInfo.billingPeriod.startDate
                                      ).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })} - ${new Date(
                                        onboardingInfo.billingPeriod.endDate
                                      ).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })}`
                                    : ''}
                                </p>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      )}
                    </AnimatedExpandingView>
                  </CenteringCol>
                }
                endElement={
                  <div className="mt-1">
                    {!isEditingBillingPeriod ? (
                      <EditIcon
                        alt={
                          'Zum Editieren des Abrechnungszeitraums klicken oder drücken Sie die Leertaste.'
                        }
                        onClick={() => setEditingBillingPeriod(true)}
                        onKeyDown={(event) => {
                          if (event.key === ' ') {
                            event.preventDefault();
                            setEditingBillingPeriod(true);
                          }
                        }}
                      />
                    ) : (
                      <div style={{ width: '14px' }}></div>
                    )}
                  </div>
                }
                additionalTestId="billing-period"
              />
              <HorizontalLine />
            </>
          )}

          <SectionSeparator />
          <ServiceContent />
          {additionalServicesSection()}
          <SectionSeparator />
          <PricingContent />
          <SectionSeparator />

          <Card className="w-100 mb-5 mx-0">
            <h4 className="my-0">{t('pageSummaryWhatElseIsImportant')}</h4>
            <Paragraph className="mb-0 fs-6">
              {t('pageSummaryWhatElseIsImportantParagraph1')}
            </Paragraph>
            <Paragraph className="mb-0 fs-6">
              {t('pageSummaryWhatElseIsImportantParagraph2')}
            </Paragraph>
          </Card>

          <Card className="w-100 mb-5 mx-0">
            <h4 className="my-0">{t('pageSummaryWhatHappensNext')}</h4>
            <Paragraph className="mb-0 fs-6">{t('pageSummaryWhatHappensNextParagraph')}</Paragraph>
          </Card>

          <SubmitView />
        </Col>
      </Row>

      <TermsAndConditionModal
        TOSModalOpen={isTermsOfServiceModalOpen}
        setTOSModalOpen={setIsTermsOfServiceModalOpen}
      />
      <ErrorModal error={error} setError={setError} />
    </Container>
  );
};

export default SummaryPage;
